import { useState } from "react";
import type { CityProgress } from "../../../../api/dispatchSupabaseApi";
import { P_THRESHOLD } from "../../../../api/dispatchSupabaseApi";

interface ReadyToDispatchProps {
  readyCities: CityProgress[];
  onCreateBatch: (city: string, orders: CityProgress["orders"]) => Promise<void>;
}

export default function ReadyToDispatch({ readyCities, onCreateBatch }: ReadyToDispatchProps) {
  const [creating, setCreating] = useState<string | null>(null);

  if (readyCities.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-8 flex flex-col items-center gap-2 text-center">
        <div className="w-8 h-8 flex items-center justify-center text-[#2C2C2C]/20">
          <i className="ri-send-plane-line text-2xl" />
        </div>
        <p className="text-sm text-[#2C2C2C]/30 font-light">
          No cities have reached {P_THRESHOLD} P yet
        </p>
        <p className="text-xs text-[#2C2C2C]/25 font-light">
          Cities will appear here once P accumulation reaches the threshold
        </p>
      </div>
    );
  }

  const handleCreate = async (cp: CityProgress) => {
    setCreating(cp.city);
    try {
      await onCreateBatch(cp.city, cp.orders);
    } finally {
      setCreating(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-[#D9D9D9]/60 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#D9D9D9]/60 bg-[#F7F6F3]">
            {["City", "Total P", "Orders", "Earliest Order", "Action"].map((h) => (
              <th key={h} className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#2C2C2C]/40 font-light">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {readyCities.map((cp) => {
            const earliest = cp.orders.length > 0
              ? new Date(Math.min(...cp.orders.map((o) => new Date(o.created_at).getTime())))
                  .toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
              : "—";

            return (
              <tr key={cp.city} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-sm font-light text-[#2C2C2C]">{cp.city}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-sm font-light text-[#2C2C2C]">
                    {cp.total_p}
                    <span className="text-[#2C2C2C]/40 ml-1">P</span>
                  </span>
                </td>
                <td className="px-5 py-3.5 text-sm text-[#2C2C2C]/60">{cp.order_count}</td>
                <td className="px-5 py-3.5 text-xs text-[#2C2C2C]/50">{earliest}</td>
                <td className="px-5 py-3.5">
                  <button
                    onClick={() => void handleCreate(cp)}
                    disabled={creating === cp.city}
                    className="flex items-center gap-2 px-4 py-1.5 bg-[#2C2C2C] text-white text-xs rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {creating === cp.city ? (
                      <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                    ) : (
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-send-plane-line text-xs" />
                      </div>
                    )}
                    {creating === cp.city ? "Creating..." : "Create Batch"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
